import { HStack, Icon, IconButton, Text, Tr, VStack } from '@chakra-ui/react';
import type { ElementType } from 'react';
import { TbCopy } from 'react-icons/tb';
import {
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
} from '../settings/primitives';
import CopyTooltip from '../CopyTooltip';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import type {
    InvoiceInboxAddresses,
    InvoiceInboxTypeAddresses,
} from '../../hooks/useGetSettings';

export const INVOICE_INBOX_TYPE_ROWS: Array<{
    key: keyof InvoiceInboxTypeAddresses;
    label: string;
}> = [
    { key: 'received', label: 'Received' },
    { key: 'returned', label: 'Credited' },
    { key: 'return_slip', label: 'Return slips' },
    { key: 'sublet', label: 'Sublets' },
];

export interface InvoiceInboxAddressesTableProps {
    addresses: InvoiceInboxAddresses;
    /** When false, hide the title + helper copy (table only). Default true. */
    showIntro?: boolean;
}

const AddressRows = ({
    typeAddresses,
    copyPrefix,
}: {
    typeAddresses: InvoiceInboxTypeAddresses;
    copyPrefix: string;
}) => {
    const { copy, isCopied } = useCopyToClipboard();

    return (
        <>
            {INVOICE_INBOX_TYPE_ROWS.map(({ key, label }) => {
                const address = typeAddresses[key];
                const copyKey = `${copyPrefix}-${key}`;
                return (
                    <Tr key={`${copyPrefix}-${key}`}>
                        <SettingsTd fontWeight="medium" color="gray.800">
                            {label}
                        </SettingsTd>
                        <SettingsTd>
                            {/* The address is generated, never typed, so it
                                reads as monospaced text rather than a field. */}
                            <Text fontFamily="mono" fontSize="xs" color="gray.700">
                                {address}
                            </Text>
                        </SettingsTd>
                        <SettingsTd>
                            <HStack justify="flex-end">
                                <CopyTooltip isCopied={isCopied(copyKey)}>
                                    <IconButton
                                        aria-label={`Copy ${label} inbox address`}
                                        icon={<Icon as={TbCopy as ElementType} boxSize={4} />}
                                        size="xs"
                                        variant="ghost"
                                        color="gray.600"
                                        _hover={{ bg: 'gray.100', color: 'gray.800' }}
                                        onClick={() => copy(address, copyKey)}
                                    />
                                </CopyTooltip>
                            </HStack>
                        </SettingsTd>
                    </Tr>
                );
            })}
        </>
    );
};

const InboxSectionTable = ({
    title,
    description,
    typeAddresses,
    copyPrefix,
}: {
    title: string;
    description: string;
    typeAddresses: InvoiceInboxTypeAddresses;
    copyPrefix: string;
}) => (
    <VStack align="stretch" spacing={2}>
        <Text fontSize="sm" fontWeight="medium" color="gray.700">
            {title}
        </Text>
        <Text fontSize="sm" color="gray.600">
            {description}
        </Text>
        <SettingsSubTable
            isInset
            head={
                <>
                    <SettingsTh>Invoice type</SettingsTh>
                    <SettingsTh>Email address</SettingsTh>
                    <SettingsTh w="56px" />
                </>
            }
        >
            <AddressRows typeAddresses={typeAddresses} copyPrefix={copyPrefix} />
        </SettingsSubTable>
    </VStack>
);

const InvoiceInboxAddressesTable = ({
    addresses,
    showIntro = true,
}: InvoiceInboxAddressesTableProps) => {
    const singleAddresses: InvoiceInboxTypeAddresses = {
        received: addresses.received,
        returned: addresses.returned,
        return_slip: addresses.return_slip,
        sublet: addresses.sublet,
    };

    return (
        <VStack align="stretch" spacing={6}>
            {showIntro && (
                <VStack align="stretch" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        Your inbox addresses
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                        Send each invoice type to its matching address. Include the shop location
                        in the subject when it is not your default location.
                    </Text>
                </VStack>
            )}

            <InboxSectionTable
                title="Single invoice"
                description="One email creates one invoice. Attach a PDF or image (PNG/JPEG)."
                typeAddresses={singleAddresses}
                copyPrefix="invoice-inbox"
            />

            {addresses.bulk && (
                <InboxSectionTable
                    title="Bulk upload"
                    description="Each attached PDF or image becomes its own invoice (up to 25 per email)."
                    typeAddresses={addresses.bulk}
                    copyPrefix="invoice-inbox-bulk"
                />
            )}
        </VStack>
    );
};

export default InvoiceInboxAddressesTable;
